import { useState, useRef } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  User, 
  Mail, 
  Lock, 
  UserCheck, 
  Eye, 
  EyeOff, 
  Loader2,
  Camera
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

const registerSchema = z
  .object({
    full_name: z.string().min(3, 'Nome muito curto'),
    email: z.string().email('Email inválido'),
    role: z.enum(['PRESIDENCIA', 'DIRETORIA', 'FINANCEIRO', 'HOMOLOGACAO', 'COLONIAL', 'JURIDICO'], {
      required_error: 'Selecione uma função',
    }),
    password: z.string().min(6, 'Mínimo 6 caracteres'),
    confirmPassword: z.string().min(6, 'Confirme sua senha'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });

type RegisterFormValues = z.infer<typeof registerSchema> & { avatarFile?: File | null };

type UserFormProps = {
  onSubmit: (values: RegisterFormValues) => Promise<void>;
  isLoading: boolean;
  initialValues?: Partial<RegisterFormValues>;
  formId?: string;
  hideSubmit?: boolean;
};

export default function UserForm({
  onSubmit,
  isLoading,
  initialValues,
  formId,
  hideSubmit,
}: UserFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [passwordValue, setPasswordValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: initialValues || {
      full_name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: undefined,
    },
  });

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: "Arquivo muito grande",
          description: "A imagem deve ter no máximo 2MB",
          variant: "destructive"
        });
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const calculateStrength = (pass: string) => {
    let score = 0;
    if (!pass) return 0;
    if (pass.length > 6) score += 20;
    if (pass.length > 10) score += 20;
    if (/[A-Z]/.test(pass)) score += 20;
    if (/[0-9]/.test(pass)) score += 20;
    if (/[^A-Za-z0-9]/.test(pass)) score += 20;
    return score;
  };

  const onFormSubmit = async (data: RegisterFormValues) => {
    await onSubmit({ ...data, avatarFile });
  };

  const PasswordStrength = ({ strength }: { strength: number }) => (
    <div className="mt-1.5 flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full transition-all duration-500 ${
            strength >= i * 20 
              ? (strength <= 40 ? 'bg-red-500' : strength <= 80 ? 'bg-amber-500' : 'bg-emerald-500')
              : 'bg-slate-100'
          }`}
        />
      ))}
    </div>
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" id={formId}>
        {/* Avatar Section */}
        <div className="col-span-2 flex flex-col items-center justify-center pb-6 border-b border-slate-100 mb-2">
          <div className="relative group">
            <Avatar className="h-24 w-24 border-4 border-white shadow-xl ring-1 ring-slate-200">
              <AvatarImage src={avatarPreview || ''} className="object-cover" />
              <AvatarFallback className="bg-slate-100 text-slate-400">
                <User className="h-10 w-10" />
              </AvatarFallback>
            </Avatar>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all transform hover:scale-110 border-2 border-white z-20"
            >
              <Camera className="h-4 w-4" />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleAvatarChange}
            />
          </div>
          <p className="text-xs text-slate-500 mt-3 font-medium">Foto de perfil (opcional)</p>
        </div>

        <FormField
          control={form.control}
          name="full_name"
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel className="text-slate-600 font-medium">Nome Completo</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4 z-10" />
                  <Input 
                    placeholder="Ex: João Silva" 
                    {...field} 
                    className="modern-input h-11 pl-11 bg-white border-slate-200 rounded-xl focus:ring-blue-500/20 focus:border-blue-500 transition-all" 
                  />
                </div>
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-slate-600 font-medium">Email Institucional</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4 z-10" />
                  <Input 
                    placeholder="exemplo@sindicato.org" 
                    type="email" 
                    {...field} 
                    className="modern-input h-11 pl-11 bg-white border-slate-200 rounded-xl focus:ring-blue-500/20 focus:border-blue-500 transition-all" 
                  />
                </div>
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-slate-600 font-medium">Função / Cargo</FormLabel>
              <div className="relative">
                <UserCheck className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4 z-10" />
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="modern-input h-11 pl-11 bg-white border-slate-200 rounded-xl focus:ring-blue-500/20 focus:border-blue-500 transition-all">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="rounded-xl shadow-xl">
                    <SelectItem value="PRESIDENCIA">Presidência</SelectItem>
                    <SelectItem value="DIRETORIA">Diretoria</SelectItem>
                    <SelectItem value="FINANCEIRO">Financeiro</SelectItem>
                    <SelectItem value="HOMOLOGACAO">Homologação</SelectItem>
                    <SelectItem value="COLONIAL">Colonial</SelectItem>
                    <SelectItem value="JURIDICO">Jurídico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-slate-600 font-medium">Senha</FormLabel>
              <FormControl>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4 z-10" />
                  <Input 
                    type={showPassword ? 'text' : 'password'} 
                    {...field} 
                    onChange={(e) => {
                      field.onChange(e);
                      setPasswordValue(e.target.value);
                    }}
                    className="modern-input h-11 pl-11 pr-11 bg-white border-slate-200 rounded-xl focus:ring-blue-500/20 focus:border-blue-500 transition-all" 
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors z-10"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormControl>
              {passwordValue && <PasswordStrength strength={calculateStrength(passwordValue)} />}
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-slate-600 font-medium">Confirmar Senha</FormLabel>
              <FormControl>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4 z-10" />
                  <Input 
                    type={showConfirmPassword ? 'text' : 'password'} 
                    {...field} 
                    className="modern-input h-11 pl-11 pr-11 bg-white border-slate-200 rounded-xl focus:ring-blue-500/20 focus:border-blue-500 transition-all" 
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors z-10"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        {!hideSubmit && (
          <div className="col-span-2 pt-4">
            <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg transition-all" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Criando conta...
                </>
              ) : (
                'Criar Conta'
              )}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}
